import { useEffect, useMemo, useState } from "react";
import {
  Product,
  ProductInput,
  ProductUnit,
  createProduct,
  deleteProduct,
  listProducts,
  updateProduct,
} from "../api/products";
import { ThemeParams } from "../telegram";
import { useKeyboardOpen } from "../hooks/useKeyboardOpen";
import { servingLabel } from "../utils/units";

type Mode = { kind: "list" } | { kind: "edit"; product: Product | null };

type FormState = {
  name: string;
  brand: string;
  kcal_100g: string;
  protein_100g: string;
  fat_100g: string;
  saturated_fat_100g: string;
  carbs_100g: string;
  sugar_100g: string;
  fiber_100g: string;
  unit: ProductUnit;
  vv_price: string;
  barcode: string;
};

const empty: FormState = {
  name: "",
  brand: "",
  kcal_100g: "",
  protein_100g: "",
  fat_100g: "",
  saturated_fat_100g: "",
  carbs_100g: "",
  sugar_100g: "",
  fiber_100g: "",
  unit: "g",
  vv_price: "",
  barcode: "",
};

function toForm(p: Product): FormState {
  return {
    name: p.name,
    brand: p.brand ?? "",
    kcal_100g: p.kcal_100g,
    protein_100g: p.protein_100g,
    fat_100g: p.fat_100g,
    saturated_fat_100g: p.saturated_fat_100g ?? "",
    carbs_100g: p.carbs_100g,
    sugar_100g: p.sugar_100g ?? "",
    fiber_100g: p.fiber_100g ?? "",
    unit: p.unit,
    vv_price: p.vv_price ?? "",
    barcode: p.barcode ?? "",
  };
}

function optNum(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function toInput(f: FormState): ProductInput {
  return {
    name: f.name.trim(),
    brand: f.brand.trim() || null,
    kcal_100g: Number(f.kcal_100g),
    protein_100g: Number(f.protein_100g),
    fat_100g: Number(f.fat_100g),
    saturated_fat_100g: optNum(f.saturated_fat_100g),
    carbs_100g: Number(f.carbs_100g),
    sugar_100g: optNum(f.sugar_100g),
    fiber_100g: optNum(f.fiber_100g),
    unit: f.unit,
    vv_price: f.vv_price.trim() ? Number(f.vv_price) : null,
    barcode: f.barcode.trim() || null,
  };
}

export type ProductsProps = {
  theme: ThemeParams;
  scheme: "light" | "dark";
  topOffset?: number;
};

export default function Products({ theme, scheme, topOffset = 0 }: ProductsProps) {
  const [items, setItems] = useState<Product[]>([]);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "list" });
  const [form, setForm] = useState<FormState>(empty);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const keyboardOpen = useKeyboardOpen();

  const bg = theme.bg_color ?? (scheme === "dark" ? "#17212b" : "#ffffff");
  const fg = theme.text_color ?? (scheme === "dark" ? "#f5f5f5" : "#1a1a1a");
  const hint = theme.hint_color ?? (scheme === "dark" ? "#708499" : "#999999");
  const btnBg = theme.button_color ?? "#3390ec";
  const btnFg = theme.button_text_color ?? "#ffffff";
  const cardBg = theme.secondary_bg_color ?? (scheme === "dark" ? "#232e3c" : "#f4f4f5");
  const inputBg = scheme === "dark" ? "#1a2733" : "#ffffff";
  const inputBorder = scheme === "dark" ? "#2c3a4a" : "#d0d4d9";
  const divider = scheme === "dark" ? "#2c3a4a" : "#e6e8eb";
  const accent = scheme === "dark" ? "#6dbf73" : "#3a9a44";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      const data = await listProducts(debouncedQ || undefined);
      setItems(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (mode.kind === "list") reload();
  }, [debouncedQ, mode.kind]);

  function startCreate() {
    setForm(empty);
    setMode({ kind: "edit", product: null });
  }

  function startEdit(p: Product) {
    setForm(toForm(p));
    setMode({ kind: "edit", product: p });
  }

  async function save() {
    if (!form.name.trim()) {
      setErr("Имя обязательно");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const payload = toInput(form);
      if (mode.kind === "edit" && mode.product) {
        await updateProduct(mode.product.id, payload);
      } else {
        await createProduct(payload);
      }
      setMode({ kind: "list" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(p: Product) {
    if (!confirm(`Удалить «${p.name}»?`)) return;
    try {
      await deleteProduct(p.id);
      setExpandedId(null);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  const inputStyle = useMemo<React.CSSProperties>(
    () => ({
      background: inputBg,
      color: fg,
      border: `1px solid ${inputBorder}`,
      borderRadius: 8,
      padding: "10px 12px",
      fontSize: 15,
      width: "100%",
      boxSizing: "border-box",
      outline: "none",
    }),
    [inputBg, fg, inputBorder]
  );

  const numInputStyle = useMemo<React.CSSProperties>(
    () => ({
      ...inputStyle,
      width: 120,
      textAlign: "right",
      padding: "8px 10px",
      fontSize: 14,
    }),
    [inputStyle]
  );

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: 13,
    color: hint,
    marginBottom: 4,
    display: "block",
  };

  const groupHeaderStyle: React.CSSProperties = {
    fontSize: 13,
    color: hint,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 8,
    marginTop: 4,
  };

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    minHeight: 36,
  };

  const rowLabelStyle: React.CSSProperties = {
    fontSize: 14,
    color: fg,
    flex: 1,
    minWidth: 0,
  };

  const subRowLabelStyle: React.CSSProperties = {
    ...rowLabelStyle,
    fontSize: 13,
    color: hint,
    paddingLeft: 14,
  };

  if (mode.kind === "edit") {
    const isNew = mode.product === null;
    const serving = servingLabel(form.unit);
    const perLabel = `на ${serving.amount} ${serving.label}`;
    return (
      <div style={{
        minHeight: "100vh",
        background: bg,
        color: fg,
        paddingTop: `calc(16px + max(${topOffset}px, env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px), var(--tg-content-safe-area-inset-top, 0px)))`,
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 16,
        boxSizing: "border-box",
        fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
      }}>
        <button onClick={() => { setErr(null); setMode({ kind: "list" }); }} style={{ background: "transparent", color: btnBg, border: "none", padding: 0, fontSize: 15, cursor: "pointer", marginBottom: 12 }}>
          ← назад
        </button>
        <h2 style={{ margin: "0 0 16px", fontSize: 20, fontWeight: 600 }}>
          {isNew ? "Новый продукт" : "Редактирование"}
        </h2>

        <div style={{ display: "grid", gap: 18 }}>
          {/* Основное */}
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label style={fieldLabelStyle}>Название *</label>
              <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={fieldLabelStyle}>Бренд</label>
              <input style={inputStyle} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div>
              <label style={fieldLabelStyle}>Единица измерения</label>
              <select style={inputStyle} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value as ProductUnit })}>
                <option value="g">граммы</option>
                <option value="ml">миллилитры</option>
                <option value="piece">штуки</option>
              </select>
            </div>
          </div>

          {/* КБЖУ */}
          <div>
            <div style={groupHeaderStyle}>КБЖУ ({perLabel})</div>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={rowStyle}>
                <span style={rowLabelStyle}>Калории, ккал *</span>
                <input style={numInputStyle} placeholder="0" inputMode="decimal" value={form.kcal_100g} onChange={(e) => setForm({ ...form, kcal_100g: e.target.value })} />
              </div>
              <div style={rowStyle}>
                <span style={rowLabelStyle}>Белки, г *</span>
                <input style={numInputStyle} placeholder="0.0" inputMode="decimal" value={form.protein_100g} onChange={(e) => setForm({ ...form, protein_100g: e.target.value })} />
              </div>
              <div style={rowStyle}>
                <span style={rowLabelStyle}>Жиры, г *</span>
                <input style={numInputStyle} placeholder="0.0" inputMode="decimal" value={form.fat_100g} onChange={(e) => setForm({ ...form, fat_100g: e.target.value })} />
              </div>
              <div style={rowStyle}>
                <span style={subRowLabelStyle}>└ насыщенные, г</span>
                <input style={numInputStyle} placeholder="0.0" inputMode="decimal" value={form.saturated_fat_100g} onChange={(e) => setForm({ ...form, saturated_fat_100g: e.target.value })} />
              </div>
              <div style={rowStyle}>
                <span style={rowLabelStyle}>Углеводы, г *</span>
                <input style={numInputStyle} placeholder="0.0" inputMode="decimal" value={form.carbs_100g} onChange={(e) => setForm({ ...form, carbs_100g: e.target.value })} />
              </div>
              <div style={rowStyle}>
                <span style={subRowLabelStyle}>└ сахар, г</span>
                <input style={numInputStyle} placeholder="0.0" inputMode="decimal" value={form.sugar_100g} onChange={(e) => setForm({ ...form, sugar_100g: e.target.value })} />
              </div>
              <div style={rowStyle}>
                <span style={subRowLabelStyle}>└ клетчатка, г</span>
                <input style={numInputStyle} placeholder="0.0" inputMode="decimal" value={form.fiber_100g} onChange={(e) => setForm({ ...form, fiber_100g: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Дополнительно */}
          <div>
            <div style={groupHeaderStyle}>Дополнительно</div>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={rowStyle}>
                <span style={rowLabelStyle}>Цена ВкусВилл, ₽</span>
                <input style={numInputStyle} placeholder="0.00" inputMode="decimal" value={form.vv_price} onChange={(e) => setForm({ ...form, vv_price: e.target.value })} />
              </div>
              <div>
                <label style={fieldLabelStyle}>Штрихкод</label>
                <input style={inputStyle} value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
              </div>
            </div>
          </div>
        </div>

        {err && <div style={{ color: "#e54848", marginTop: 12, fontSize: 13 }}>{err}</div>}

        <button onClick={save} disabled={submitting} style={{ marginTop: 18, background: btnBg, color: btnFg, border: "none", borderRadius: 10, padding: "12px 18px", fontSize: 15, fontWeight: 500, width: "100%", cursor: submitting ? "wait" : "pointer" }}>
          {submitting ? "..." : isNew ? "Создать" : "Сохранить"}
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: bg,
      color: fg,
      paddingTop: `calc(16px + max(${topOffset}px, env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px), var(--tg-content-safe-area-inset-top, 0px)))`,
      paddingLeft: 20,
      paddingRight: 20,
      paddingBottom: "calc(164px + max(env(safe-area-inset-bottom), var(--tg-safe-area-inset-bottom, 0px)))",
      boxSizing: "border-box",
      fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
    }}>
      <h2 style={{ margin: "0 0 12px", fontSize: 20, fontWeight: 600 }}>Каталог продуктов</h2>

      <input
        style={inputStyle}
        placeholder="Поиск по названию или бренду…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      {err && <div style={{ color: "#e54848", marginTop: 12, fontSize: 13 }}>{err}</div>}

      <div style={{ marginTop: 16, background: cardBg, borderRadius: 12, overflow: "hidden" }}>
        {loading && <div style={{ color: hint, fontSize: 13, padding: 14 }}>загрузка…</div>}
        {!loading && items.length === 0 && (
          <div style={{ color: hint, fontSize: 13, padding: 14 }}>{debouncedQ ? "ничего не найдено" : "пусто — добавьте первый продукт"}</div>
        )}
        {items.map((p, idx) => {
          const isOpen = expandedId === p.id;
          const isLast = idx === items.length - 1;
          return (
            <div key={p.id} style={{ borderBottom: isLast ? "none" : `1px solid ${divider}` }}>
              <button
                onClick={() => setExpandedId(isOpen ? null : p.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "12px 14px",
                  textAlign: "left",
                  cursor: "pointer",
                  color: fg,
                  fontFamily: "inherit",
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.25, wordBreak: "break-word" }}>
                    {p.name}
                    {p.brand && (
                      <span style={{ fontWeight: 400, color: hint }}> ({p.brand})</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: accent, marginTop: 3 }}>
                    {servingLabel(p.unit).amount} {servingLabel(p.unit).label} · {Number(p.kcal_100g).toFixed(0)} ккал
                  </div>
                </div>
                <div style={{ color: hint, fontSize: 18, lineHeight: 1, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 150ms" }}>›</div>
              </button>

              {isOpen && (
                <div style={{ padding: "4px 14px 14px" }}>
                  <div style={{ fontSize: 12, color: hint, marginBottom: 8 }}>
                    Порция: {servingLabel(p.unit).amount} {servingLabel(p.unit).label}
                  </div>
                  <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: hint }}>Калории</span>
                      <span>{Number(p.kcal_100g).toFixed(0)} ккал</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: hint }}>Белки</span>
                      <span>{Number(p.protein_100g).toFixed(1)} г</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: hint }}>Жиры</span>
                      <span>{Number(p.fat_100g).toFixed(1)} г</span>
                    </div>
                    {p.saturated_fat_100g != null && (
                      <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: 14, fontSize: 12, color: hint }}>
                        <span>насыщенные</span>
                        <span>{Number(p.saturated_fat_100g).toFixed(1)} г</span>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: hint }}>Углеводы</span>
                      <span>{Number(p.carbs_100g).toFixed(1)} г</span>
                    </div>
                    {p.sugar_100g != null && (
                      <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: 14, fontSize: 12, color: hint }}>
                        <span>сахар</span>
                        <span>{Number(p.sugar_100g).toFixed(1)} г</span>
                      </div>
                    )}
                    {p.fiber_100g != null && (
                      <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: 14, fontSize: 12, color: hint }}>
                        <span>клетчатка</span>
                        <span>{Number(p.fiber_100g).toFixed(1)} г</span>
                      </div>
                    )}
                  </div>

                  {p.vv_price && (
                    <div style={{ marginTop: 10, fontSize: 13, color: hint }}>
                      ВкусВилл: <span style={{ color: fg }}>{Number(p.vv_price).toFixed(2)} ₽</span>
                    </div>
                  )}

                  <div style={{ marginTop: 12, display: "flex", gap: 16 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); startEdit(p); }}
                      style={{ background: "transparent", color: btnBg, border: "none", padding: 0, fontSize: 13, cursor: "pointer" }}
                    >
                      Изменить
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(p); }}
                      style={{ background: "transparent", color: "#e54848", border: "none", padding: 0, fontSize: 13, cursor: "pointer" }}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={startCreate}
        aria-label="Добавить продукт"
        style={{
          position: "fixed",
          right: 20,
          bottom: "calc(92px + max(env(safe-area-inset-bottom), var(--tg-safe-area-inset-bottom, 0px)))",
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: btnBg,
          color: btnFg,
          border: "none",
          fontSize: 28,
          lineHeight: 1,
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
          display: keyboardOpen ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        +
      </button>
    </div>
  );
}
